import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LockKeyhole, LogIn } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { LogoLockup } from "../components/site/Logo";
import SEO from "../components/site/SEO";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <div className="min-h-screen bg-[#050507]" />;
  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Logged in");
      nav("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050507] text-white flex items-center justify-center px-6 py-16 relative overflow-hidden">
      <SEO title="Quasect Admin — Login" description="Quasect admin console" path="/admin/login" />
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-40" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md border border-white/10 bg-[#0A0A0D] p-8 md:p-10 noise"
      >
        <LogoLockup variant="dark" />
        <div className="mt-8 font-mono text-[10px] tracking-[0.25em] uppercase text-[#00F0FF] flex items-center gap-2">
          <LockKeyhole className="w-3 h-3" /> ADMIN CONSOLE
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl mt-3 tracking-tight">
          Sign in
        </h1>
        <p className="text-[#8B93A5] mt-3 text-sm">
          Restricted access. SOC analysts and authorized staff only.
        </p>
        <form onSubmit={submit} data-testid="admin-login-form" className="mt-7 space-y-4">
          <Field label="Email">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email"
              required
              autoFocus
              className={inputCls}
              placeholder="admin@quasect.com"
            />
          </Field>
          <Field label="Password">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password"
              required
              className={inputCls}
              placeholder="••••••••"
            />
          </Field>
          <button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-submit"
            className="btn-sweep w-full bg-[#39FF14] text-black font-bold uppercase tracking-wider px-7 py-4 text-sm hover:bg-[#32E612] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" /> Sign In
              </>
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

const inputCls =
  "w-full bg-black border border-white/15 text-white placeholder:text-[#5d6373] px-4 py-3.5 outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] transition-colors font-mono text-sm";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-[#8B93A5] mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
